import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api/useAuth';
import { api } from '../api/client';
import { Stack, Row, Text, Card, Button, Input, Panel, Avatar, EmptyState, Tag, List, ListItem, Editable, Divider } from '../ui';
import { GroupLists } from './GroupLists';
import { ArchivedDealsPanel } from './ArchivedDealsPanel';

const SECTOR_OPTIONS = ['Energy','LNG','Oil & Gas','Power','Carbon','Mining','Soft Commodities','Metals','Banking','Capital Markets','Sovereign Capital','Funds','Tech','Infrastructure','Government','Geopolitics','Other'];

function DeletedDocumentsPanel() {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.deletedArtifacts();
      setDocs(r.artifacts ?? []);
    } catch {} finally { setLoading(false); }
  }

  async function restore(id: string) {
    setRestoring(id);
    try {
      await api.restoreArtifact(id);
      setDocs(prev => prev.filter(d => d.id !== id));
    } catch (e: any) { alert(e.message); } finally { setRestoring(null); }
  }

  return (
    <Stack gap={2}>
      <button onClick={() => { setOpen(o => !o); if (!open) load(); }}
        className="flex items-center justify-between w-full text-left">
        <Text variant="small" className="font-medium">Deleted documents{docs.length > 0 ? ` (${docs.length})` : ''}</Text>
        <Text variant="label" muted>{open ? '\u25b2' : '\u25bc'}</Text>
      </button>
      {open && (
        <Stack gap={2}>
          {loading && <Text variant="label" muted>loading…</Text>}
          {!loading && docs.length === 0 && <Text variant="label" muted>no deleted documents</Text>}
          {docs.map(d => (
            <Row key={d.id} justify="between" align="center">
              <Stack gap={0}>
                <Text variant="small">{d.title}</Text>
                <Text variant="label" muted>{d.type}{d.deal_id ? ` · deal ${d.deal_id?.slice(0, 8)}` : ''}</Text>
              </Stack>
              <Button variant="ghost" size="sm" disabled={restoring === d.id} onClick={() => restore(d.id)}>
                {restoring === d.id ? 'restoring…' : 'Restore'}
              </Button>
            </Row>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export function Settings() {
  const { friend, refresh } = useAuth();
  const [avatarVersion, setAvatarVersion] = useState(Date.now());
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteResult, setInviteResult] = useState<{ url: string; email: string; expiresInDays: number } | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteErr, setInviteErr] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [capabilities, setCapabilities] = useState<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.invitations().then(r => setInvitations(r.invitations)).catch(() => {});
    fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/agent/capabilities`, { credentials: 'include' })
      .then(r => r.json()).then(setCapabilities).catch(() => {});
  }, []);

  async function uploadAvatar(file: File) {
    setAvatarBusy(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/avatar/upload`, {
        method: 'PUT', credentials: 'include',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      setAvatarVersion(j.version);
      refresh();
    } catch (e: any) {
      alert('upload failed: ' + e.message);
    } finally {
      setAvatarBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function deleteAvatar() {
    setAvatarBusy(true);
    try {
      await fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/avatar/delete`, { method: 'DELETE', credentials: 'include' });
      setAvatarVersion(Date.now());
      refresh();
    } finally { setAvatarBusy(false); }
  }

  async function sendInvite() {
    setInviteBusy(true); setInviteErr(null); setInviteResult(null);
    try {
      const r = await api.inviteFriend(inviteEmail.trim(), inviteName.trim() || undefined);
      setInviteResult({ url: r.inviteUrl, email: inviteEmail.trim(), expiresInDays: 14 });
      setInviteEmail(''); setInviteName('');
      api.invitations().then(r => setInvitations(r.invitations)).catch(() => {});
    } catch (e: any) {
      setInviteErr(e.message);
    } finally { setInviteBusy(false); }
  }

  async function copy(text: string) {
    try { await navigator.clipboard.writeText(text); } catch {}
  }

  if (!friend) return null;

  const sectors = (friend.sectors as string[] | null) ?? [];

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <Text variant="display">Settings</Text>
        <Text muted variant="small">Your profile, your friends, your agents.</Text>
      </Stack>

      <Panel title="Profile">
        <Stack gap={3}>
          <Row gap={3} align="center">
            <Avatar name={friend.name} friendId={friend.id} hasAvatar={!!(friend as any).avatarUrl} size="xl" version={avatarVersion} />
            <Stack gap={1} className="flex-1">
              <Row gap={1}>
                <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={avatarBusy}>{avatarBusy ? 'uploading…' : 'upload photo'}</Button>
                {(friend as any).avatarUrl && <Button variant="ghost" size="sm" onClick={deleteAvatar} disabled={avatarBusy}>remove</Button>}
              </Row>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadAvatar(e.target.files[0])} />
              <Text variant="label">jpg or png, up to 5mb. shown across the app wherever your name appears.</Text>
            </Stack>
          </Row>
          <Divider />
          <Stack gap={2}>
            <Row gap={2}>
              <Stack gap={0.5} className="flex-1">
                <Text variant="micro">name</Text>
                <Editable
                  className="text-base"
                  value={friend.name}
                  onSave={async v => { await api.updateMe({ name: v }); refresh(); }}
                />
              </Stack>
              <Stack gap={0.5} className="flex-1">
                <Text variant="micro">email</Text>
                <Text variant="body">{friend.email}</Text>
              </Stack>
            </Row>
            <Row gap={2}>
              <Stack gap={0.5} className="flex-1">
                <Text variant="micro">city</Text>
                <Editable
                  className="text-base"
                  value={friend.city ?? ''}
                  empty="add your home city"
                  onSave={async v => { await api.updateMe({ city: v }); refresh(); }}
                />
              </Stack>
              <Stack gap={0.5} className="flex-1">
                <Text variant="micro">timezone</Text>
                <Editable
                  className="text-base"
                  value={friend.timezone ?? ''}
                  empty="add your timezone"
                  onSave={async v => { await api.updateMe({ timezone: v }); refresh(); }}
                />
              </Stack>
            </Row>
            <Stack gap={1}>
              <Text variant="micro">sectors</Text>
              <Row gap={1} wrap>
                {SECTOR_OPTIONS.map(s => {
                  const on = sectors.includes(s);
                  return (
                    <button
                      key={s}
                      onClick={async () => {
                        const next = on ? sectors.filter(x => x !== s) : [...sectors, s];
                        await api.updateMe({ sectors: next });
                        refresh();
                      }}
                      className={`h-9 sm:h-7 px-3 sm:px-2 rounded-sm text-xs transition-colors hairline ${on ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
                      {s}
                    </button>
                  );
                })}
              </Row>
            </Stack>
          </Stack>
        </Stack>
      </Panel>

      <Panel title="Location sharing">
        <Stack gap={2}>
          <Text variant="small" muted>
            when on, the app pings your current city to your circle so the hangs agent can spot overlaps and suggest meetups. you control the precision.
          </Text>
          <Row gap={1} wrap>
            {[
              { value: 'off',     label: 'off',                hint: 'no location shared' },
              { value: 'city',    label: 'share city',         hint: 'just the city, not your gps' },
              { value: 'precise', label: 'share precise (gps)', hint: 'helps with same-city venue suggestions' },
            ].map(o => {
              const on = ((friend as any).locationSharing ?? 'off') === o.value;
              return (
                <button
                  key={o.value}
                  onClick={async () => { await api.setLocationSharing(o.value as any); refresh(); }}
                  className={`text-left h-auto px-2 py-2 rounded-sm hairline transition-colors ${on ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
                  <div className="text-sm font-medium">{o.label}</div>
                  <div className="text-[10px] opacity-70">{o.hint}</div>
                </button>
              );
            })}
          </Row>
          {(friend as any).currentCity && (
            <Text variant="label">currently shared: {(friend as any).currentCity}{(friend as any).currentCountry ? `, ${(friend as any).currentCountry}` : ''} (last update {(friend as any).currentAt ? new Date((friend as any).currentAt).toLocaleString() : '—'})</Text>
          )}
        </Stack>
      </Panel>

      <Panel title="Invite a friend">
        <Stack gap={2}>
          <Text variant="small" muted>create a link they can use to sign up. invitations are bound to their email and expire in 14 days.</Text>
          <Row gap={1}>
            <Input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="their name (optional)" className="flex-1" />
            <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="their email" className="flex-1" />
            <Button variant="primary" onClick={sendInvite} disabled={inviteBusy || !inviteEmail.includes('@')}>{inviteBusy ? 'creating…' : 'create invite'}</Button>
          </Row>
          {inviteErr && <Text variant="small" className="text-negative">{inviteErr}</Text>}
          {inviteResult && (
            <Card className="bg-whisper">
              <Stack gap={1}>
                <Text variant="micro">invite for {inviteResult.email}</Text>
                <Row gap={1}>
                  <input className="text-xs hairline rounded-sm px-2 py-1.5 bg-bg flex-1 tnum" readOnly value={inviteResult.url} onClick={e => e.currentTarget.select()} />
                  <Button variant="secondary" size="sm" onClick={() => copy(inviteResult.url)}>copy</Button>
                </Row>
                <Text variant="label">share this with {inviteResult.email}. expires in {inviteResult.expiresInDays} days.</Text>
              </Stack>
            </Card>
          )}
          {invitations.length > 0 && (
            <Stack gap={1}>
              <Text variant="micro">recent invitations</Text>
              <List>
                {invitations.slice(0, 8).map(inv => (
                  <ListItem key={inv.id}>
                    <Row justify="between" align="center">
                      <Stack gap={0.5}>
                        <Text variant="small">{inv.email}</Text>
                        <Text variant="label">
                          {inv.acceptedAt ? `joined ${new Date(inv.acceptedAt).toLocaleDateString()}` : `pending — expires ${new Date(inv.expiresAt).toLocaleDateString()}`}
                        </Text>
                      </Stack>
                      {!inv.acceptedAt && <Tag>pending</Tag>}
                      {inv.acceptedAt && <Tag>joined</Tag>}
                    </Row>
                  </ListItem>
                ))}
              </List>
            </Stack>
          )}
        </Stack>
      </Panel>

      <GroupLists />

      <Panel title="Prosimos Deal Submission Portal">
        <Stack gap={2}>
          <Text variant="small" muted>Share this link with counterparties to submit encrypted deal opportunities directly to your group. Submitters' data and uploaded files (CIS + SCO + FCO) are end-to-end encrypted in their browser before upload. Only Inner Circle members can decrypt them.</Text>
          <div className="flex items-center gap-2 bg-whisper rounded-sm px-3 py-2">
            <code className="flex-1 text-xs font-mono text-muted break-all select-all">https://app.ardacious.app/prosimos/submit</code>
            <Button variant="secondary" size="sm" onClick={() => copy('https://app.ardacious.app/prosimos/submit')}>Copy</Button>
          </div>
          <Text variant="label" muted>After submission, a KYC/DD report is automatically generated and appears in Opportunities before you accept or reject.</Text>
        </Stack>
      </Panel>


      <Panel title="Your agents" action={capabilities ? <Tag>{capabilities.chatModel}</Tag> : undefined}>
        {!capabilities ? <Text variant="label">loading…</Text> : (
          <Stack gap={2}>
            <Text variant="small" muted>each specialist runs in the background, produces real artifacts in your vault, and can be addressed directly from the chat panel.</Text>
            <List>
              {capabilities.specialists.map((s: any) => (
                <ListItem key={s.id}>
                  <Stack gap={0.5}>
                    <Text variant="small" className="font-medium">{s.label}</Text>
                    <Text variant="label">{s.capabilities.join(' · ')}</Text>
                  </Stack>
                </ListItem>
              ))}
            </List>
            <Divider />
            <Stack gap={1}>
              <Text variant="micro">live integrations</Text>
              <Row gap={0.5} wrap>{capabilities.integrations.live.map((i: string) => <Tag key={i}>{i}</Tag>)}</Row>
            </Stack>
            <Stack gap={1}>
              <Text variant="micro">stubbed (swap in when keys arrive)</Text>
              <Row gap={0.5} wrap>{capabilities.integrations.stubbed.map((i: string) => <Tag key={i}>{i}</Tag>)}</Row>
            </Stack>
          </Stack>
        )}
      </Panel>

      <div className="opacity-70 hover:opacity-100 transition-opacity mt-6">
        <Stack gap={2}>
          <Text variant="micro" muted>Hidden lists</Text>
          <ArchivedDealsPanel kind="archived" />
          <ArchivedDealsPanel kind="deleted" />
        </Stack>
      </div>

      <div className="hairline rounded-sm p-3">
        <DeletedDocumentsPanel />
      </div>
    </Stack>
  );
}